import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { 
  UserPlus, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  MapPin, 
  Calendar,
  Briefcase,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Check
} from 'lucide-react';
import './FormsUnified.css';

const DEFAULT_FORM_DATA = {
  kicker: 'Open Positions',
  title: 'Join Our Growing Team',
  description: 'Explore current opportunities and discover how you can make an impact in our school community. Apply today to become part of our mission.',
  items: [
    {
      id: 'primary-teacher',
      title: 'Primary Classroom Teacher',
      location: 'Jakarta Campus',
      type: 'Full-time',
      status: 'open',
      deadline: '2026-06-30',
      summary: 'Lead engaging classroom instruction and support student growth in grades 1-6.',
      description: 'We are seeking an experienced and passionate Primary Classroom Teacher to join our dynamic team. You will create an engaging learning environment that fosters academic excellence and personal growth.',
      responsibilities: [
        'Plan and deliver daily lessons aligned with curriculum goals',
        'Create a supportive classroom environment that encourages participation',
        'Collaborate with grade-level teams and parents',
        'Assess student progress and provide constructive feedback',
        'Participate in professional development activities'
      ],
      requirements: [
        'Bachelor in Education or related field',
        '2+ years teaching experience preferred',
        'Strong classroom management and communication skills',
        'Proficiency in English and Indonesian',
        'Commitment to student-centered learning'
      ],
      applyNote: 'Email CV to hr@permataharapan.sch.id'
    },
    {
      id: 'ict-lab-assistant',
      title: 'ICT Lab Assistant',
      location: 'Bekasi Campus',
      type: 'Full-time',
      status: 'open',
      deadline: '2026-05-31',
      summary: 'Support digital learning, maintain lab equipment, and assist teachers during ICT sessions.',
      description: 'Join our IT team to support the school\'s digital transformation. You will maintain our ICT infrastructure and help students develop essential technology skills.',
      responsibilities: [
        'Maintain computers, projectors, and lab software',
        'Assist teachers with digital learning activities',
        'Provide basic tech support for students and staff',
        'Monitor lab equipment and report maintenance needs',
        'Conduct basic troubleshooting and system updates'
      ],
      requirements: [
        'Diploma/Bachelor in IT or related field',
        'Basic networking and troubleshooting skills',
        'Friendly, service-oriented communication',
        'Problem-solving mindset',
        'Willingness to learn new technologies'
      ],
      applyNote: 'Submit application via HR contact or school office'
    }
  ]
};

export default function CareersPositionsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    items: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [expandedIndex, setExpandedIndex] = useState(0);

  // Inputs for adding new responsibility or requirement item
  const [newRespText, setNewRespText] = useState({});
  const [newReqText, setNewReqText] = useState({});

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=careers`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'careers-positions' || s.section_type === 'careers-open-positions' || s.section_type === 'open-positions');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=careers`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'careers-positions' || s.section_type === 'careers-open-positions' || s.section_type === 'open-positions');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawItems = Array.isArray(en.items) && en.items.length > 0 
          ? en.items 
          : (Array.isArray(en.open_positions) && en.open_positions.length > 0 ? en.open_positions : DEFAULT_FORM_DATA.items);

        const loaded = {
          kicker: en.kicker || 'Open Positions',
          title: en.title || 'Join Our Growing Team',
          description: en.description || DEFAULT_FORM_DATA.description,
          items: rawItems.map(item => ({
            id: item.id || '',
            title: item.title || '',
            location: item.location || 'Jakarta Campus',
            type: item.type || 'Full-time',
            status: item.status || 'open',
            deadline: item.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            summary: item.summary || '',
            description: item.description || '',
            responsibilities: Array.isArray(item.responsibilities) ? item.responsibilities : (typeof item.responsibilities === 'string' ? item.responsibilities.split('\n').filter(Boolean) : []),
            requirements: Array.isArray(item.requirements) ? item.requirements : (typeof item.requirements === 'string' ? item.requirements.split('\n').filter(Boolean) : []),
            applyNote: item.applyNote || item.apply_note || 'Email CV to hr@permataharapan.sch.id'
          }))
        };

        setFormData(loaded);
        setOriginalData(loaded);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, description: false, items: false };
              const pItems = {};
              const pDeletes = {};

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'description', 'items'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  } else if (c.field_id?.includes('items.')) {
                    const m = c.field_id.match(/items\.(\d+)\.(add|update|delete)/);
                    if (m) {
                      const idx = m[1];
                      const op = m[2];
                      if (op === 'delete') {
                        pDeletes[`item_${idx}`] = true;
                      } else {
                        pItems[`item_${idx}`] = true;
                      }
                    } else {
                      const mOld = c.field_id.match(/items\.(\d+)/);
                      if (mOld) pItems[`item_${mOld[1]}`] = true;
                    }
                  }
                }
              });

              setPendingFields(pFields);
              setPendingItems(pItems);
              setPendingDeletes(pDeletes);
            }
          }
        } catch (pe) {
          console.warn('[CareersPositionsForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[CareersPositionsForm] Load error:', err);
      toast.error('Gagal memuat data Open Positions');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.items];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, items: next };
    });
  };

  const handleAddResponsibility = (posIdx) => {
    const text = (newRespText[posIdx] || '').trim();
    if (!text) return;
    setFormData(prev => {
      const next = [...prev.items];
      const resps = [...(next[posIdx].responsibilities || []), text];
      next[posIdx] = { ...next[posIdx], responsibilities: resps };
      return { ...prev, items: next };
    });
    setNewRespText(prev => ({ ...prev, [posIdx]: '' }));
    toast.success('Poin tanggung jawab ditambahkan');
  };

  const handleRemoveResponsibility = (posIdx, respIdx) => {
    setFormData(prev => {
      const next = [...prev.items];
      const resps = (next[posIdx].responsibilities || []).filter((_, idx) => idx !== respIdx);
      next[posIdx] = { ...next[posIdx], responsibilities: resps };
      return { ...prev, items: next };
    });
  };

  const handleAddRequirement = (posIdx) => {
    const text = (newReqText[posIdx] || '').trim();
    if (!text) return;
    setFormData(prev => {
      const next = [...prev.items];
      const reqs = [...(next[posIdx].requirements || []), text];
      next[posIdx] = { ...next[posIdx], requirements: reqs };
      return { ...prev, items: next };
    });
    setNewReqText(prev => ({ ...prev, [posIdx]: '' }));
    toast.success('Poin kualifikasi ditambahkan');
  };

  const handleRemoveRequirement = (posIdx, reqIdx) => {
    setFormData(prev => {
      const next = [...prev.items];
      const reqs = (next[posIdx].requirements || []).filter((_, idx) => idx !== reqIdx);
      next[posIdx] = { ...next[posIdx], requirements: reqs };
      return { ...prev, items: next };
    });
  };

  const handleAddPosition = () => {
    const newPos = {
      id: `position-${Date.now()}`,
      title: 'Posisi Lowongan Baru',
      location: 'Jakarta Campus',
      type: 'Full-time',
      status: 'open',
      deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      summary: 'Ringkasan singkat posisi ini...',
      description: 'Penjelasan lengkap cakupan tugas dan peran ini...',
      responsibilities: ['Tanggung jawab utama 1', 'Tanggung jawab utama 2'],
      requirements: ['Kualifikasi pendidikan / pengalaman 1', 'Kualifikasi 2'],
      applyNote: 'Email CV ke hr@permataharapan.sch.id',
      isNew: true
    };

    setFormData(prev => ({
      ...prev,
      items: [newPos, ...prev.items]
    }));
    setExpandedIndex(0);
    toast.success('Posisi lowongan baru ditambahkan ke draf. Isi data dan klik "Save Lowongan Ini".');
  };

  const handleRemovePosition = async (index) => {
    const itemToRemove = formData.items[index];

    // If item is new/draft not saved on server yet, remove locally
    if (itemToRemove.isNew && (!originalData.items || !originalData.items[index])) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index)
      }));
      toast.info(`Lowongan baru dibatalkan`);
      return;
    }

    if (formData.items.length <= 1) {
      toast.warning('Minimal harus ada 1 posisi lowongan');
      return;
    }

    if (!window.confirm(`Ajukan penghapusan lowongan "${itemToRemove.title || 'Untitled'}" ke Kepala Sekolah?`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const key = `del_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.delete`,
          field_name: `Hapus Lowongan: ${itemToRemove.title}`,
          old_value: originalData.items?.[index] || itemToRemove,
          new_value: { action: 'delete', id: itemToRemove.id, title: itemToRemove.title },
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan hapus lowongan "${itemToRemove.title}" berhasil dikirim untuk approval`);
        setPendingDeletes(prev => ({ ...prev, [`item_${index}`]: true }));
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersPositionsForm] Submit delete error:', err);
      toast.error('Gagal mengajukan hapus lowongan: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersPositionsForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmitPosition = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const item = formData.items[index];
    const key = `item_${index}`;
    const isNew = item.isNew || !originalData.items || !originalData.items[index];
    const fieldId = isNew ? `items.${index}.add` : `items.${index}.update`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const cleanItem = { ...item };
      delete cleanItem.isNew;

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: `Job Vacancy: ${item.title || `Position ${index + 1}`}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: cleanItem,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Lowongan "${item.title}" berhasil diajukan untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        setFormData(prev => {
          const next = [...prev.items];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_careers');
          sessionStorage.removeItem('cms_sections_careers');
          window.dispatchEvent(new Event('cms-careers-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[CareersPositionsForm] Submit error:', err);
      toast.error('Gagal mengajukan lowongan: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Open Positions Section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <UserPlus size={22} />
        </div>
        <div>
          <h2 className="unified-title">Open Positions (Job Vacancies)</h2>
          <p className="unified-subtitle">
            Kelola lowongan pekerjaan guru dan staf, lokasi kampus, status pendaftaran, tenggat waktu (deadline), kualifikasi, serta instruksi lamaran.
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Open Positions"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Join Our Growing Team"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Section Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={2}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={pendingFields.description}
            placeholder="Explore current opportunities and discover how you can make an impact..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', formData.description, 'Section Description')}
            disabled={pendingFields.description || submitting.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : (pendingFields.description ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Open Positions List */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">Daftar Lowongan Kerja ({formData.items.length} Posisi)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Lowongan dengan status "Open" dan tenggat waktu aktif otomatis tampil pada kartu interaktif dan modal detail di halaman Careers.
            </p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleAddPosition}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={16} /> Tambah Posisi Lowongan
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
          {formData.items.map((item, idx) => {
            const isPending = pendingItems[`item_${idx}`];
            const isDeletePending = pendingDeletes[`item_${idx}`];
            const isSubmitting = submitting[`item_${idx}`] || submitting[`del_${idx}`];
            const isExpanded = expandedIndex === idx;

            return (
              <div
                key={idx}
                style={{
                  border: isDeletePending 
                    ? '1.5px dashed #ef4444' 
                    : (isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0'),
                  borderRadius: '14px',
                  background: isDeletePending ? '#fef2f2' : (isPending ? '#fffdf7' : '#ffffff'),
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                  overflow: 'hidden'
                }}
              >
                {/* Accordion Card Header */}
                <div
                  style={{
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: isExpanded ? '#f8fafc' : '#ffffff',
                    borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                  }}
                  onClick={() => setExpandedIndex(isExpanded ? null : idx)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ 
                      background: item.status === 'open' ? '#dcfce7' : '#fee2e2', 
                      color: item.status === 'open' ? '#15803d' : '#b91c1c', 
                      fontSize: '11px', 
                      fontWeight: 800, 
                      padding: '3px 10px', 
                      borderRadius: '9999px',
                      textTransform: 'uppercase'
                    }}>
                      {item.status === 'open' ? 'OPEN NOW' : 'CLOSED'}
                    </span>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                      {item.title || 'Posisi Tanpa Judul'}
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '13px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={13} /> {item.location}
                      </span>
                      <span>•</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Briefcase size={13} /> {item.type}
                      </span>
                      <span>•</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={13} /> Deadline: {item.deadline}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {isDeletePending && (
                      <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                        <Clock size={11} /> Pending Delete
                      </span>
                    )}
                    {!isDeletePending && isPending && (
                      <span className="unified-pending-pill">
                        <Clock size={12} /> Pending Approval
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemovePosition(idx);
                      }}
                      disabled={isDeletePending || isSubmitting}
                      style={{ 
                        background: isDeletePending ? '#fee2e2' : 'none', 
                        border: 'none', 
                        color: '#ef4444', 
                        padding: '6px', 
                        cursor: isDeletePending ? 'not-allowed' : 'pointer',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px'
                      }}
                      title="Hapus Lowongan"
                    >
                      <Trash2 size={16} />
                      {isDeletePending && <span>Pending Delete</span>}
                    </button>
                    {isExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
                  </div>
                </div>

                {/* Expanded Form Details */}
                {isExpanded && (
                  <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Row 1: Title & Status */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '14px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Judul Posisi *
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.title || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'title', e.target.value)}
                          placeholder="e.g. Primary Classroom Teacher"
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Status Lowongan
                        </label>
                        <select
                          className="unified-input"
                          value={item.status || 'open'}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'status', e.target.value)}
                        >
                          <option value="open">Buka (Open)</option>
                          <option value="closed">Tutup (Closed)</option>
                        </select>
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Tipe Kontrak
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.type || 'Full-time'}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'type', e.target.value)}
                          placeholder="e.g. Full-time"
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Batas Deadline *
                        </label>
                        <input
                          type="date"
                          className="unified-input"
                          value={item.deadline || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'deadline', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Row 2: Location & Apply Note */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Lokasi Kampus
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.location || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'location', e.target.value)}
                          placeholder="e.g. Jakarta Campus"
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                          Instruksi Pengiriman Lamaran (How to Apply)
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.applyNote || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleItemChange(idx, 'applyNote', e.target.value)}
                          placeholder="e.g. Email CV to hr@permataharapan.sch.id"
                        />
                      </div>
                    </div>

                    {/* Row 3: Summary (Card Lead) */}
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                        Ringkasan Kartu / Lead Summary (Tampil di preview kartu)
                      </label>
                      <input
                        type="text"
                        className="unified-input"
                        value={item.summary || ''}
                        disabled={isDeletePending || isPending}
                        onChange={(e) => handleItemChange(idx, 'summary', e.target.value)}
                        placeholder="Lead engaging classroom instruction and support student growth in grades 1-6."
                      />
                    </div>

                    {/* Row 4: Full Description (Modal View) */}
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', display: 'block' }}>
                        Deskripsi Lengkap Posisi (Tampil di modal "About This Role")
                      </label>
                      <textarea
                        className="unified-textarea"
                        rows={2}
                        value={item.description || ''}
                        disabled={isDeletePending || isPending}
                        onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        placeholder="We are seeking an experienced and passionate Primary Classroom Teacher to join our dynamic team..."
                      />
                    </div>

                    {/* Row 5: Responsibilities List */}
                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                        Tanggung Jawab Pekerjaan (Responsibilities)
                      </label>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        {(item.responsibilities || []).map((resp, rIdx) => (
                          <div key={rIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <CheckCircle2 size={14} color="#2563eb" />
                            <span style={{ fontSize: '13px', color: '#1e293b', flex: 1 }}>{resp}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveResponsibility(idx, rIdx)}
                              disabled={isDeletePending || isPending}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: (isDeletePending || isPending) ? 'not-allowed' : 'pointer', padding: 0 }}
                              title="Hapus poin"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          className="unified-input"
                          value={newRespText[idx] || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => setNewRespText(prev => ({ ...prev, [idx]: e.target.value }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddResponsibility(idx);
                            }
                          }}
                          placeholder="Tulis tanggung jawab baru..."
                          style={{ fontSize: '12px', padding: '8px 12px' }}
                        />
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          disabled={isDeletePending || isPending}
                          onClick={() => handleAddResponsibility(idx)}
                          style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          + Tambah Poin
                        </button>
                      </div>
                    </div>

                    {/* Row 6: Requirements List */}
                    <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                        Kualifikasi / Persyaratan (Requirements)
                      </label>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                        {(item.requirements || []).map((req, qIdx) => (
                          <div key={qIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#ffffff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <Check size={14} color="#059669" />
                            <span style={{ fontSize: '13px', color: '#1e293b', flex: 1 }}>{req}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveRequirement(idx, qIdx)}
                              disabled={isDeletePending || isPending}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: (isDeletePending || isPending) ? 'not-allowed' : 'pointer', padding: 0 }}
                              title="Hapus poin"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          className="unified-input"
                          value={newReqText[idx] || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => setNewReqText(prev => ({ ...prev, [idx]: e.target.value }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddRequirement(idx);
                            }
                          }}
                          placeholder="Tulis kualifikasi / persyaratan baru..."
                          style={{ fontSize: '12px', padding: '8px 12px' }}
                        />
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          disabled={isDeletePending || isPending}
                          onClick={() => handleAddRequirement(idx)}
                          style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                        >
                          + Tambah Kualifikasi
                        </button>
                      </div>
                    </div>

                    {/* Card Submit Action */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
                      <button
                        type="button"
                        className="unified-submit-button"
                        onClick={() => handleSubmitPosition(idx)}
                        disabled={isDeletePending || isPending || isSubmitting}
                      >
                        <Save size={14} />
                        {isSubmitting ? 'Saving...' : (isDeletePending ? 'Pending Delete' : (isPending ? 'Pending' : 'Save Lowongan Ini'))}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
